import type { IMenuProps, IOmit } from '@sixui/ui';
import {
  faCopy,
  faTrashCan,
  faUpRightFromSquare,
} from '@fortawesome/pro-light-svg-icons';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Menu, MenuItem } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiLegalDocument } from '~/lib/api/modules/LegalDocuments';
import { copyToClipboard } from '~/helpers/copyToClipboard';
import { useDeleteLegalDocumentMutation } from '~/lib/api/modules/LegalDocuments/hooks/useDeleteLegalDocumentMutation';

export type ILegalDocumentListItemMenuProps = IContainerProps &
  IOmit<IMenuProps, 'trigger' | 'children'> & {
    legalDocument: IApiLegalDocument;
  };

export const LegalDocumentListItemMenu: React.FC<
  ILegalDocumentListItemMenuProps
> = (props) => {
  const { sx, legalDocument, ...other } = props;
  const { t } = useTranslation();
  const deleteLegalDocumentMutation = useDeleteLegalDocumentMutation();
  const processing = deleteLegalDocumentMutation.isLoading;

  const handleDelete = (): void => {
    if (deleteLegalDocumentMutation.isLoading) {
      return;
    }

    // eslint-disable-next-line no-alert
    const doDeletion = confirm(
      t('LegalDocumentListItem.dialog.confirmDeletion.text', {
        title: legalDocument.title,
      }),
    );

    if (doDeletion) {
      void deleteLegalDocumentMutation
        .mutateAsync({
          appId: legalDocument.appId,
          legalDocumentId: legalDocument._id,
        })
        .catch(() => {
          // TODO: handle error (snackbar)
        });
    }
  };

  return (
    <Menu
      trigger={({ getProps }) => (
        <IconButton
          icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
          loading={processing}
          {...getProps()}
        />
      )}
      placement="bottom-end"
      sx={sx}
      {...other}
    >
      {legalDocument.url ? (
        <MenuItem
          label={t('LegalDocumentListItemMenu.option.openUrl.label')}
          leadingIcon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
          href={legalDocument.url}
          target="_blank"
        />
      ) : null}
      <MenuItem
        label={t('LegalDocumentListItemMenu.option.copyLink.label')}
        leadingIcon={<FontAwesomeIcon icon={faCopy} />}
        onClick={() => copyToClipboard(legalDocument.url)}
      />
      <MenuItem
        label={t('common.delete')}
        leadingIcon={<FontAwesomeIcon icon={faTrashCan} />}
        onClick={handleDelete}
        variant="danger"
      />
    </Menu>
  );
};
